import React, { useEffect, useRef } from 'react';

import { FormattedMessage } from 'react-intl';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import ProgressBar from '@atlaskit/progress-bar';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { PollingForUpgradeQuery } from './__generated__/PollingForUpgradeQuery.graphql';
import type { Edition, Product } from './PlaceOrderLite';
import { type OperationalEvent, useOperationalAnalytics } from './usePollingAnalytics';

const POLL_TIMEOUT = 120_000;
const POLL_INTERVAL = 2 * 1000;

const ProductKeyToHAMSMap: Record<Product, string> = {
	['confluence']: 'confluence',
	['jira']: 'jira-software',
	['jira-product-discovery']: 'jira-product-discovery',
	['jira-service-management']: 'jira-servicedesk',
};

function isValidEdition(edition: unknown): edition is Edition {
	if (typeof edition !== 'string') {
		return false;
	}

	return ['free', 'standard', 'premium'].includes(edition);
}

async function getJiraEdition(product: Product): Promise<Edition> {
	const response = await fetch('/rest/internal/latest/meta/edition');

	if (!response.ok) {
		throw new Error(`Fetching upgrade status failed with ${response.status}`);
	}

	const data = (await response.json()) as Partial<Record<string, Edition | unknown>>;

	const edition = data[ProductKeyToHAMSMap[product]];

	if (!isValidEdition(edition)) {
		throw new Error(`Invalid edition ${edition}`);
	}

	return edition;
}

async function getConfluenceEdition(environment: RelayModernEnvironment): Promise<Edition> {
	const response = await fetchQuery<PollingForUpgradeQuery>(
		environment,
		graphql`
			query PollingForUpgradeQuery {
				confluence_tenantContext {
					editions {
						confluence
					}
				}
			}
		`,
		{},
	).toPromise();

	const confluence = response?.confluence_tenantContext?.editions?.confluence?.toLowerCase();

	if (!isValidEdition(confluence)) {
		throw new Error(`Invalid edition ${confluence}`);
	}

	return confluence;
}

async function getEdition(product: Product, environment: RelayModernEnvironment): Promise<Edition> {
	switch (product) {
		case 'confluence':
			return getConfluenceEdition(environment);
		case 'jira':
		case 'jira-product-discovery':
		case 'jira-service-management':
			return getJiraEdition(product);
		default:
			throw new Error(`Unsupported product ${product}`);
	}
}

function pollForUpgrade(
	product: Product,
	edition: Edition,
	environment: RelayModernEnvironment,
	sendAnalyticsEvents: (event: OperationalEvent) => void,
) {
	const pollStartTime = Date.now();
	const pollTimeoutTime = new Date(pollStartTime + POLL_TIMEOUT).getTime();
	let numberOfAttempts = 0;

	sendAnalyticsEvents({
		action: 'start',
		actionSubject: 'pollForUpgrade',
		attributes: {
			timestamp: pollStartTime,
			package: 'place-order-lite',
		},
	});

	async function poll(resolve: (value: unknown) => void, reject: (error: Error) => void) {
		numberOfAttempts++;
		try {
			if (Date.now() > pollTimeoutTime) {
				sendAnalyticsEvents({
					action: 'timeout',
					actionSubject: 'pollForUpgrade',
					attributes: {
						totalAttempts: numberOfAttempts,
						totalTimeTakenInMs: Date.now() - pollStartTime,
						package: 'place-order-lite',
					},
				});
				return reject(new Error('Timeout'));
			}

			const currentEdition = (await getEdition(product, environment)).toLowerCase();

			if (currentEdition === edition) {
				sendAnalyticsEvents({
					action: 'success',
					actionSubject: 'pollForUpgrade',
					attributes: {
						totalAttempts: numberOfAttempts,
						timeElapsed: Date.now() - pollStartTime,
						package: 'place-order-lite',
						currentEdition,
					},
				});

				return resolve(true);
			}
			setTimeout(() => {
				poll(resolve, reject);
			}, POLL_INTERVAL);
		} catch (err) {
			sendAnalyticsEvents({
				action: 'error',
				actionSubject: 'pollForUpgrade',
				attributes: {
					totalAttempts: numberOfAttempts,
					reason: err instanceof Error ? err.message : 'Unknown error',
					package: 'place-order-lite',
				},
			});

			return reject(err instanceof Error ? err : new Error('Unknown issue in polling'));
		}
	}

	return new Promise((resolve, reject) => poll(resolve, reject));
}

interface PollingForUpgradeProps {
	edition: Edition;
	isQuiet?: boolean;
	onComplete: () => void;
	onError: (err: Error) => void;
	product: Product;
}

export function PollingForUpgrade({
	onComplete,
	onError,
	product,
	edition,
	isQuiet,
}: PollingForUpgradeProps): React.JSX.Element | null {
	const environment = useRelayEnvironment();

	const isPolling = useRef(false);

	const sendAnalyticsEvents = useOperationalAnalytics({ product, edition });

	const experimentConfig = FeatureGates.getExperiment('place_order_lite_wait');

	const waitTime =
		product === 'confluence'
			? experimentConfig.get('confluence', 42)
			: experimentConfig.get('jira', 12);

	sendAnalyticsEvents({
		action: 'setup',
		actionSubject: 'pollForUpgrade',
		attributes: {
			waitTime,
			product,
		},
	});
	useEffect(() => {
		if (isPolling.current) {
			return;
		}

		const timeoutId = setTimeout(() => {
			isPolling.current = true;
			pollForUpgrade(product, edition, environment, sendAnalyticsEvents)
				.then(() => onComplete())
				.catch(onError);
		}, waitTime);

		return () => clearTimeout(timeoutId);
	}, [product, edition, onComplete, onError, environment, sendAnalyticsEvents, waitTime]);

	if (isQuiet) {
		return null;
	}

	return (
		<Box>
			<Stack space="space.150">
				<ProgressBar isIndeterminate />
				<Text align="center">
					<FormattedMessage
						defaultMessage="Upgrading your workspace, less than a minute remaining..."
						description="The workspace is being upgraded for this customer"
						id="place-order-lite.polling-for-upgrade.upgrading-message"
					/>
				</Text>
			</Stack>
		</Box>
	);
}

export const PollingForUpgradeWithAtlassianRelayEnvironmentProvider = (
	props: PollingForUpgradeProps,
): React.JSX.Element => (
	<AtlassianRelayEnvironmentProvider>
		<PollingForUpgrade {...props} />
	</AtlassianRelayEnvironmentProvider>
);
